'use client';
import styled, { DefaultTheme } from 'styled-components';

import { breakpoints } from '@styles/breakpoint';
import { Spacers } from '@styles/theme';

import { StyledButtonProps } from '@components/Button/interfaces';

const getHoverBackgroundColor = (props: StyledButtonProps & { theme: DefaultTheme }) => {
  if (props.$disabled) {
    return props.$variant === 'primary'
      ? props.theme.colors.actionInactive
      : props.theme.colors.backgroundAccent;
  }

  return props.$variant === 'primary'
    ? props.theme.colors.actionHover
    : props.theme.colors.backgroundDark;
};

const getBackgroundColor = (props: StyledButtonProps & { theme: DefaultTheme }) => {
  if (props.$disabled) {
    return props.$variant === 'primary'
      ? props.theme.colors.actionInactive
      : props.theme.colors.backgroundAccent;
  }

  return props.$variant === 'primary' ? props.theme.colors.actionDefault : 'none';
};

const getColor = (props: StyledButtonProps & { theme: DefaultTheme }) => {
  if (props.$disabled) {
    return props.theme.colors.typographyBodyLight;
  }

  return props.$variant === 'primary'
    ? props.theme.colors.typographyHeadersDark
    : props.theme.colors.typographyHeadersLight;
};

const getHoverColor = (props: StyledButtonProps & { theme: DefaultTheme }) => {
  if (props.$disabled) {
    return props.theme.colors.typographyBodyLight;
  }

  return props.theme.colors.typographyHeadersDark;
};

const getActiveBackgroundColor = (props: StyledButtonProps & { theme: DefaultTheme }) => {
  return props.$variant === 'primary'
    ? props.theme.colors.actionActive
    : props.theme.colors.backgroundDark;
};

const getActiveColor = (props: StyledButtonProps & { theme: DefaultTheme }) => {
  return props.theme.colors.typographyHeadersDark;
};

export const ButtonContainer = styled.button<StyledButtonProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${Spacers.XS}rem;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  width: 100%;
  padding: ${Spacers.SM}rem ${Spacers.BASE}rem;
  background-color: ${getBackgroundColor};
  border-radius: ${Spacers.XS}rem;
  border: ${(props) => {
    if (props.$disabled) {
      return 'none';
    }
    return props.$variant !== 'primary' ? `1px solid ${props.theme.colors.backgroundDark}` : 'none';
  }};
  color: ${getColor};
  font-size: ${(props) => props.theme.typography.title.fontSize};
  font-weight: ${(props) => props.theme.typography.title.fontWeight};
  line-height: ${(props) => props.theme.typography.title.lineHeight};

  &:hover {
    background-color: ${getHoverBackgroundColor};
    color: ${getHoverColor};
  }

  &:active {
    background-color: ${getActiveBackgroundColor};
    color: ${getActiveColor};
  }
`;

export const StyledSchoolCodeSearchButton = styled(ButtonContainer)<{ $hasError?: boolean }>`
  justify-content: flex-start;
  align-items: center;
  gap: ${Spacers.BASE}rem;
  padding: ${Spacers.XS}rem ${Spacers.BASE}rem;
  background-color: ${(props) => props.theme.colors.backgroundAccent};
  border: ${(props) => (props.$hasError ? `1px solid ${props.theme.colors.statusError}` : 'none')};
  color: ${(props) => props.theme.colors.typographyBodyLight};
  font-size: ${(props) => props.theme.typography.body.fontSize};
  font-weight: ${(props) => props.theme.typography.body.fontWeight};
  line-height: ${(props) => props.theme.typography.body.lineHeight};

  ${breakpoints.mobile} {
    max-width: 100%;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.backgroundAccent};
    color: ${(props) => props.theme.colors.typographyBodyLight};
  }

  &:active {
    background-color: ${(props) => props.theme.colors.backgroundAccent};
    color: ${(props) => props.theme.colors.typographyBodyLight};
  }

  &:focus {
    color: ${(props) =>
    props.$hasError ? props.theme.colors.statusError : props.theme.colors.typographyHeadersLight};
  }
`;

export const TextWrapper = styled.span`
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-align: left;

  ${breakpoints.mobile} {
    max-width: calc(100% - 1.5rem);
  }
`;

export const SegmentButtonContainer = styled.button<{ $active: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  padding: ${Spacers.SM}rem ${Spacers.BASE}rem;
  background-color: ${(props) =>
    props.$active ? props.theme.colors.brandAccent : props.theme.colors.backgroundAccent};
  border-radius: ${Spacers.XXL}rem;
  color: ${(props) =>
    props.$active ? props.theme.colors.brand : props.theme.colors.typographyBodyLight};
  font-size: ${(props) => props.theme.typography.title.fontSize};
  font-weight: ${(props) => props.theme.typography.title.fontWeight};
  line-height: ${(props) => props.theme.typography.title.lineHeight};

  /* Only show hover state on inactive tabs */
  &:hover {
    color: ${
  (props) =>
    props.$active
      ? props.theme.colors.brand // Keep active color on hover for active tabs
      : props.theme.colors.typographyHeadersLight // Show hover color for inactive tabs
};
  }

  &:active {
    background-color: ${(props) => props.theme.colors.brandAccent};
    color: ${(props) => props.theme.colors.brand};
  }
`;
